import React, {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
} from 'react';
import classNames from 'classnames';
import { useLocation } from 'react-router-dom';

import {
  Datalist,
  Button,
} from '@ktag/styleguide/dist/react';

import {
  PaymentStore,
  FormConfigStore,
  ArticlesStore,
  MunicipalityStore,
  ArticleInfo,
  DeliveryInfoSummary,
  TypeCreateOrderFN,
  DateFormatter,
  NewPaymentAndDelivery,
  PaymentAndDelivery,
} from 'ewp-common';

import orderProcess from '../../../services/orderProcess';
import formDataStore from '../../../stores/formDataStore';

interface Props {
  id?: string;
  className?: string;
  additionalClassNames?: string;
  setRoute?: Dispatch<SetStateAction<number>>;
  createOrderFn:TypeCreateOrderFN
}

const Payment = (props: Props) => {
  const {
    id,
    className,
    additionalClassNames,
    setRoute,
    createOrderFn,
  } = props;

  const {
    state: articlesState,
    fetchArticles,
    totalAmount,
  } = useContext(ArticlesStore.ArticlesContext);

  const amountCurrency = articlesState?.articles?.data?.[0]?.currency_ISO;

  const {
    state: formConfigState,
  } = useContext(FormConfigStore.FormConfigContext);

  const {
    state: formDataState,
  } = useContext(formDataStore.FormDataContext);

  const {
    state: municipalityState,
  } = useContext(MunicipalityStore.MunicipalityContext);

  const {
    state: paymentState,
  } = useContext(PaymentStore.PaymentContext);

  const createOrder = async () => {
    const formDataValue = {
      ...formDataState,
      municipalityId: municipalityState.selectedMunicipality?.data?.gemeinde?.gemeinde_ID,
    };
    const reqData = {
      formData: formDataValue,
      amountData: {
        amount: articlesState.amount,
        currency: amountCurrency,
      },
      paymentMethod: paymentState.selectedPaymentMethod,
      paymentCollectionType: paymentState.paymentCollectionType,
      deliveryType: paymentState.deliveryType
    };
    createOrderFn(orderProcess.createOrder,
      reqData,
      formConfigState?.data?.labelsMap?.errorLabel,
      () => setRoute(4));
  };

  const {
    personal,
    deliveryInfo,
    otherPersonInfo,
  } = formDataState;

  const { search } = useLocation();
  const status = search && new URLSearchParams(search).get('status');

  const fieldConfigs = formConfigState?.data?.labelsMap;

  const {
    fieldAnrede,
    fieldName,
    fieldFirstname,
    fieldAhvNumber,
    fieldDob,
    fieldStrasse,
    fieldNr,
    fieldPlz,
    fieldOrt,
    fieldLand,
    fieldPhone,
    fieldEmail,
    fieldLockType,
    uploadfield,
    fieldRemarks
  } = personal;
  const municipalityId = municipalityState.selectedMunicipality?.data?.gemeinde?.gemeinde_ID;

  useEffect(() => {
    if (status === 'success') {
      setRoute(4);
    }
    if (municipalityId) {
      fetchArticles(municipalityId);
    }
    window.scrollTo(0, 0);
  }, []);

  let isDeliveryAddressSchweiz = true;

  if(deliveryInfo?.fieldDeliveryAddressDifferent != null && ((deliveryInfo?.fieldDeliveryAddressDifferent === 'Ja' && deliveryInfo?.fieldLand != 'Schweiz') || (deliveryInfo?.fieldDeliveryAddressDifferent === 'Nein' && fieldLand != 'Schweiz'))){
    isDeliveryAddressSchweiz = false;
  }

  return (
    <div id={id} className={classNames(className, additionalClassNames)}>
      {/* <h2>{data.h2OrderOverview.labelDisplayName}</h2> */}
      <h3 className="mt-4">{fieldConfigs.h2Personalien.labelDisplayName}</h3>
      <Datalist
        items={[
          {
            key: fieldConfigs.fieldAnrede?.labelDisplayName,
            value: fieldAnrede,
          },
          {
            key: fieldConfigs.fieldName?.labelDisplayName,
            value: fieldName,
          },
          {
            key: fieldConfigs.fieldFirstname?.labelDisplayName,
            value: fieldFirstname,
          },
          {
            key: fieldConfigs.fieldAhvNumber?.labelDisplayName,
            value: fieldAhvNumber,
          },
          {
            key: fieldConfigs.fieldDob?.labelDisplayName,
            value: DateFormatter.formatDateForDisplay(fieldDob),
          },
          {
            key: fieldConfigs.fieldStrasseNr?.labelDisplayName,
            value: `${fieldStrasse} ${fieldNr}`,
          },
          {
            key: fieldConfigs.fieldPlzOrt?.labelDisplayName,
            value: `${fieldPlz} ${fieldOrt}`,
          },
          {
            key: fieldConfigs.fieldLand?.labelDisplayName,
            value: fieldLand,
          },
          {
            key: fieldConfigs.fieldPhone?.labelDisplayName,
            value: fieldPhone,
          },
          {
            key: fieldConfigs.fieldEmail?.labelDisplayName,
            value: fieldEmail,
          },
          {
            key: fieldConfigs?.fieldLockType?.labelDisplayName ?? "Angaben zur Sperre",
            value: fieldLockType,
          },
          {
            key: fieldConfigs?.uploadfield?.labelDisplayName,
            value: uploadfield?.fileName,
          },
        ]}
      />
      <Datalist
        items={[
          {
            key: fieldConfigs.fieldRemarks?.labelDisplayName,
            value: fieldRemarks
          },
        ]}
      />

      <ArticleInfo
        municipalityId={municipalityId}
        labelConfig={fieldConfigs}
        deliveryAddressCountry={deliveryInfo?.fieldDeliveryAddressDifferent != null && deliveryInfo?.fieldDeliveryAddressDifferent === 'Ja' ? deliveryInfo?.fieldLand : personal?.fieldLand}
        selectedDeliveryCode={paymentState.deliveryType}
        isPersonUnderage={false}
      />

      {process.env.REACT_APP_USE_NEW_PAYMENT_AND_DELIVERY_METHOD == "1" ?
      <NewPaymentAndDelivery
        municipalityId={municipalityId}
        labelConfig={fieldConfigs}
        showPaymentOption={true}
        showDeliveryOption={true}
        isDeliveryAddressSchweiz={isDeliveryAddressSchweiz}
      /> :
      <PaymentAndDelivery
        municipalityId={municipalityId}
        labelConfig={fieldConfigs}
        showPaymentOption={true}
        showDeliveryOption={true}
      />}

      <div className="payment-actions">
        <Button
          label={fieldConfigs.btnCancel?.labelDisplayName}
          onClick={() => {
            setRoute(2);
            window.location.reload;
          }}
        />
        <Button
          label={fieldConfigs.btnCompleteOrder?.labelDisplayName}
          onClick={createOrder}
          primary
          disabled={
            (!paymentState?.selectedDeliveryMethod)
            || (paymentState.paymentMethods?.data?.length > 0 && !paymentState.selectedPaymentMethod)
            || (paymentState.selectedPaymentMethod?.value?.toLowerCase() === '3' && !paymentState.paymentCollectionType)
            || (paymentState.selectedDeliveryMethod?.deliveryCode?.toLowerCase() === '3' && !paymentState.deliveryType)
          }
        />
      </div>

    </div>
  );
};

Payment.defaultProps = {
  id: '',
  className: 'payment',
  additionalClassNames: '',
};

export default Payment;
